import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from '../utils/s3Upload'
import { API_URL } from '../config/api'

const MAX_ATTACHMENTS = 10

const ATTACHMENT_ACCEPT =
  '.pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.webp,application/pdf,image/*'

function parsePriceApiResponse(raw) {
  let responseData = raw
  if (typeof responseData === 'string') {
    try {
      responseData = JSON.parse(responseData)
    } catch {
      responseData = raw
    }
  }
  if (responseData?.body != null) {
    const b = responseData.body
    if (typeof b === 'string') {
      try {
        responseData = JSON.parse(b)
      } catch {
        responseData = b
      }
    } else {
      responseData = b
    }
  }
  // Some APIs nest again under data as a JSON string
  if (responseData?.data != null && typeof responseData.data === 'string') {
    try {
      responseData = {
        ...responseData,
        data: JSON.parse(responseData.data)
      }
    } catch {
      // leave as-is
    }
  }
  return responseData
}

function isPriceApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function getPresignedUploadUrl(meta) {
  return String(
    meta?.uploadUrl ||
      meta?.url ||
      meta?.presignedUrl ||
      meta?.signedUrl ||
      ''
  ).trim()
}

function normalizePresignFileMetas(responseData, files, contentTypesForPresign) {
  if (!isPriceApiSuccess(responseData)) return null

  const dataBlock =
    responseData?.data && typeof responseData.data === 'object'
      ? responseData.data
      : responseData
  const nestedBlock =
    dataBlock?.data && typeof dataBlock.data === 'object' ? dataBlock.data : null

  const fallbackType = (idx) =>
    contentTypesForPresign?.[idx] ||
    resolveFileContentType(files[idx]) ||
    'application/octet-stream'

  const mapMeta = (meta, idx) => ({
    uploadUrl: getPresignedUploadUrl(meta),
    s3Key: meta?.s3Key || meta?.key,
    originalFileName:
      meta?.originalFileName || meta?.fileName || files[idx]?.name,
    contentType: meta?.contentType || fallbackType(idx)
  })

  for (const list of [dataBlock?.files, responseData?.files, nestedBlock?.files]) {
    if (Array.isArray(list) && list.length > 0) {
      return list.map((meta, idx) => mapMeta(meta, idx))
    }
  }

  for (const list of [
    dataBlock?.uploadUrls,
    nestedBlock?.uploadUrls,
    responseData?.uploadUrls
  ]) {
    if (Array.isArray(list) && list.length > 0) {
      const source = dataBlock?.uploadUrls
        ? dataBlock
        : nestedBlock || responseData
      return list.map((uploadUrl, idx) => ({
        uploadUrl,
        s3Key: source?.s3Keys?.[idx],
        originalFileName: source?.originalFileNames?.[idx] || files[idx]?.name,
        contentType: source?.contentTypes?.[idx] || fallbackType(idx)
      }))
    }
  }

  const single =
    (getPresignedUploadUrl(dataBlock) && dataBlock?.s3Key && dataBlock) ||
    (getPresignedUploadUrl(nestedBlock) && nestedBlock?.s3Key && nestedBlock) ||
    (getPresignedUploadUrl(responseData) && responseData?.s3Key && responseData)

  if (single && files.length === 1) {
    return [mapMeta(single, 0)]
  }

  return null
}

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function isCompleteDateValue(value) {
  if (!DATE_INPUT_PATTERN.test(value)) return false
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(parsed.getTime())
}

function getDateRangeError(validFrom, validTill) {
  if (!isCompleteDateValue(validFrom) || !isCompleteDateValue(validTill)) return ''
  if (validTill < validFrom) return 'Valid Till cannot be before Valid From'
  return ''
}

function dateInputToIsoStart(dateStr) {
  if (!dateStr) return ''
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString()
}

function dateInputToIsoEnd(dateStr) {
  if (!dateStr) return ''
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString()
}

const PriceTrackerAdd = () => {
  const navigate = useNavigate()

  const [suppliers, setSuppliers] = useState([])
  const [filteredSuppliers, setFilteredSuppliers] = useState([])
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('')
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1)
  const [isSuppliersLoading, setIsSuppliersLoading] = useState(true)
  const [selectedSuppliers, setSelectedSuppliers] = useState([])

  const [formData, setFormData] = useState({
    productName: '',
    purchaseUom: '',
    purchasePricePerUnit: '',
    salesUom: '',
    salesPricePerUnit: '',
    priceValidFrom: '',
    priceValidTill: '',
    remarks: '',
    procurementId: ''
  })

  const [attachments, setAttachments] = useState([])
  const [productImageFile, setProductImageFile] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dateRangeError, setDateRangeError] = useState('')
  const [unitOptions, setUnitOptions] = useState([])
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)

  const fetchUnits = useCallback(async () => {
    setIsLoadingUnits(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-units',
        page: 1,
        limit: 500,
        sortBy: 'unit',
        sortOrder: 'asc'
      })
      const responseData = parsePriceApiResponse(response.data)
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : Array.isArray(responseData?.units)
          ? responseData.units
          : []
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || '',
          unit: String(item?.unit || '').trim(),
          status: String(item?.status || '').trim()
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === 'active'
        )
      setUnitOptions(isPriceApiSuccess(responseData) ? activeUnits : [])
    } catch (error) {
      console.error('Fetch units error:', error)
      setUnitOptions([])
      toast.error('Failed to load units', { autoClose: 2000 })
    } finally {
      setIsLoadingUnits(false)
    }
  }, [])

  useEffect(() => {
    fetchUnits()
  }, [fetchUnits])

  useEffect(() => {
    const fetchSuppliers = async () => {
      setIsSuppliersLoading(true)
      try {
        const payload = { action: 'get-active-suppliers' }
        const response = await axios.post(API_URL, payload)

        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
          } catch (e) {
            responseData = response.data
          }
        }
        if (responseData && responseData.body) {
          if (typeof responseData.body === 'string') {
            try {
              responseData = JSON.parse(responseData.body)
            } catch (e) {
              responseData = responseData.body
            }
          } else {
            responseData = responseData.body
          }
        }

        if (responseData?.success === true && Array.isArray(responseData.data)) {
          setSuppliers(responseData.data)
          setFilteredSuppliers(responseData.data)
        } else {
          toast.error(responseData?.message || 'Failed to fetch suppliers', {
            autoClose: 2000
          })
        }
      } catch (error) {
        console.error('Fetch suppliers error:', error)
        toast.error('Failed to load suppliers', { autoClose: 2000 })
      } finally {
        setIsSuppliersLoading(false)
      }
    }

    fetchSuppliers()
  }, [])

  const selectedFilesCount = attachments.length

  const attachmentsLabel = useMemo(() => {
    if (selectedFilesCount === 0) return 'No files selected'
    return `${selectedFilesCount} file${selectedFilesCount > 1 ? 's' : ''} selected`
  }, [selectedFilesCount])

  const productImageLabel = useMemo(() => {
    if (!productImageFile) return 'No image selected'
    return productImageFile.name
  }, [productImageFile])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    if (name === 'priceValidFrom' || name === 'priceValidTill') {
      setDateRangeError('')
    }
  }

  const handleDateBlur = (e) => {
    const { name, value } = e.target
    if (name !== 'priceValidFrom' && name !== 'priceValidTill') return

    const validFrom = name === 'priceValidFrom' ? value : formData.priceValidFrom
    const validTill = name === 'priceValidTill' ? value : formData.priceValidTill
    const rangeError = getDateRangeError(validFrom, validTill)

    setDateRangeError(rangeError)
    if (!rangeError) return

    if (name === 'priceValidTill' && isCompleteDateValue(validTill)) {
      setFormData((prev) => ({ ...prev, priceValidTill: '' }))
      return
    }

    if (
      name === 'priceValidFrom' &&
      isCompleteDateValue(validFrom) &&
      isCompleteDateValue(validTill)
    ) {
      setFormData((prev) => ({ ...prev, priceValidTill: '' }))
    }
  }

  const handleDecimalPriceChange = (e) => {
    const { name, value } = e.target
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleSupplierSearchChange = (e) => {
    const searchValue = e.target.value
    setSupplierSearchTerm(searchValue)
    setShowSupplierDropdown(true)
    setSelectedSupplierIndex(-1)

    if (searchValue.trim() === '') {
      setFilteredSuppliers(suppliers)
    } else {
      const filtered = suppliers.filter((supplier) =>
        String(supplier.clientname || '')
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      )
      setFilteredSuppliers(filtered)
    }
  }

  const handleSupplierSelect = (supplier) => {
    const supplierId = supplier._id || ''
    const supplierName = supplier.clientname || ''
    if (!supplierId) return

    setSupplierSearchTerm('')
    setShowSupplierDropdown(false)
    setSelectedSupplierIndex(-1)

    setSelectedSuppliers((prev) => {
      if (prev.some((s) => s._id === supplierId)) return prev
      return [...prev, { _id: supplierId, clientname: supplierName }]
    })
  }

  const handleSupplierKeyDown = (e) => {
    if (!showSupplierDropdown || filteredSuppliers.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedSupplierIndex((prev) =>
          prev < filteredSuppliers.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedSupplierIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (
          selectedSupplierIndex >= 0 &&
          selectedSupplierIndex < filteredSuppliers.length
        ) {
          handleSupplierSelect(filteredSuppliers[selectedSupplierIndex])
        } else if (filteredSuppliers.length === 1) {
          handleSupplierSelect(filteredSuppliers[0])
        }
        break
      case 'Escape':
        setShowSupplierDropdown(false)
        setSelectedSupplierIndex(-1)
        break
      default:
        break
    }
  }

  const handleAttachmentsChange = (e) => {
    const picked = Array.from(e.target.files || [])
    e.target.value = ''
    if (picked.length === 0) return

    setAttachments((prev) => {
      const combined = [...prev, ...picked]
      if (combined.length > MAX_ATTACHMENTS) {
        toast.error(`Max ${MAX_ATTACHMENTS} attachments allowed`, { autoClose: 2000 })
        return prev
      }
      return combined
    })
  }

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const clearAllAttachments = () => {
    setAttachments([])
  }

  const handleProductImageChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) {
      setProductImageFile(null)
      return
    }
    if (!String(file.type || '').startsWith('image/')) {
      toast.error('Please choose an image file', { autoClose: 2000 })
      e.target.value = ''
      return
    }
    setProductImageFile(file)
    e.target.value = ''
  }

  const presignAndUploadFiles = async (files, currentUserId, label = 'attachments') => {
    if (!files || files.length === 0) return []

    if (selectedSuppliers.length === 0) {
      toast.error('Please select a supplier before uploading files', { autoClose: 2000 })
      return null
    }

    const contentTypesForPresign = files.map((f) => resolveFileContentType(f))
    const supplierIdForPrefix = selectedSuppliers[0]?._id
    const payload = {
      action: 'get-price-upload-presigned-url',
      supplierId: supplierIdForPrefix,
      files: files.map((f, idx) => ({
        fileName: f.name,
        contentType: contentTypesForPresign[idx]
      })),
      fileNames: files.map((f) => f.name),
      contentTypes: contentTypesForPresign,
      createdBy: currentUserId
    }

    const response = await axios.post(API_URL, payload)
    const responseData = parsePriceApiResponse(response.data)
    const fileMetas = normalizePresignFileMetas(
      responseData,
      files,
      contentTypesForPresign
    )

    if (!fileMetas || fileMetas.length !== files.length) {
      toast.error(responseData?.message || `Failed to get ${label} upload URLs`, {
        autoClose: 2000
      })
      return null
    }

    // S3 PUT via raw fetch — never shared API axios (currentUserId ~44B bug).
    try {
      for (let idx = 0; idx < files.length; idx += 1) {
        const fileMeta = fileMetas[idx]
        const file = files[idx]
        const contentType =
          fileMeta.contentType ||
          contentTypesForPresign[idx] ||
          'application/octet-stream'
        await putFileToPresignedUrl(fileMeta.uploadUrl, file, contentType)
      }
    } catch (error) {
      console.error(`Upload price ${label} error:`, error)
      toast.error(error?.message || `Failed to upload ${label}`, { autoClose: 2500 })
      return null
    }

    return fileMetas.map((f, idx) => ({
      s3Key: f.s3Key,
      originalFileName: f.originalFileName || files[idx].name,
      contentType:
        f.contentType || contentTypesForPresign[idx] || 'application/octet-stream'
    }))
  }

  const uploadAttachmentsIfNeeded = async (currentUserId) => {
    if (!attachments || attachments.length === 0) return []
    return presignAndUploadFiles(attachments, currentUserId, 'attachments')
  }

  /**
   * Product images must use keys under Product_attachment/ — separate presign from quote files (Price-attachment/).
   */
  const presignAndUploadProductImage = async (file, currentUserId) => {
    if (!file) return null

    if (selectedSuppliers.length === 0) {
      toast.error('Please select a supplier before uploading product image', {
        autoClose: 2000
      })
      return null
    }

    const contentTypeForPresign = resolveFileContentType(file)
    const supplierIdForPrefix = selectedSuppliers[0]?._id
    const payload = {
      action: 'get-price-product-image-upload-presigned-url',
      fileNames: [file.name],
      contentTypes: [contentTypeForPresign],
      supplierId: supplierIdForPrefix,
      createdBy: currentUserId
    }

    const response = await axios.post(API_URL, payload)
    const responseData = parsePriceApiResponse(response.data)
    const fileMetas = normalizePresignFileMetas(
      responseData,
      [file],
      [contentTypeForPresign]
    )

    if (!fileMetas || fileMetas.length !== 1) {
      toast.error(
        responseData?.message || 'Failed to get product image upload URL',
        { autoClose: 2000 }
      )
      return null
    }

    const fileMeta = fileMetas[0]
    const contentType =
      fileMeta.contentType || contentTypeForPresign || 'application/octet-stream'

    try {
      await putFileToPresignedUrl(fileMeta.uploadUrl, file, contentType)
    } catch (error) {
      console.error('Upload product image error:', error)
      toast.error(error?.message || 'Failed to upload product image', {
        autoClose: 2500
      })
      return null
    }

    const s3Key = fileMeta.s3Key
    if (s3Key && !String(s3Key).startsWith('Product_attachment/')) {
      console.warn('Product image s3Key does not start with Product_attachment/:', s3Key)
    }

    return {
      s3Key,
      originalFileName: fileMeta.originalFileName || fileMeta.fileName || file.name,
      contentType: fileMeta.contentType || contentType
    }
  }

  const uploadProductImageIfNeeded = async (currentUserId) => {
    if (!productImageFile) return null
    return presignAndUploadProductImage(productImageFile, currentUserId)
  }

  const validateForm = () => {
    if (selectedSuppliers.length === 0) {
      toast.error('Please select at least one supplier', { autoClose: 2000 })
      return false
    }
    if (!formData.productName.trim()) {
      toast.error('Product name is required', { autoClose: 2000 })
      return false
    }
    if (!formData.purchaseUom.trim()) {
      toast.error('Purchase UOM is required', { autoClose: 2000 })
      return false
    }
    const purchasePrice = parseFloat(formData.purchasePricePerUnit)
    if (
      formData.purchasePricePerUnit === '' ||
      Number.isNaN(purchasePrice) ||
      purchasePrice < 0
    ) {
      toast.error('Valid purchase price per unit is required', { autoClose: 2000 })
      return false
    }
    const salesPriceRaw = String(formData.salesPricePerUnit || '').trim()
    if (salesPriceRaw !== '') {
      const salesPrice = parseFloat(salesPriceRaw)
      if (Number.isNaN(salesPrice) || salesPrice < 0) {
        toast.error('Sales price per unit must be a valid non-negative number when provided', {
          autoClose: 2000
        })
        return false
      }
    }
    if (!isCompleteDateValue(formData.priceValidFrom)) {
      toast.error('Valid from date is required', { autoClose: 2000 })
      return false
    }
    if (!isCompleteDateValue(formData.priceValidTill)) {
      toast.error('Valid till date is required', { autoClose: 2000 })
      return false
    }

    const rangeError = getDateRangeError(
      formData.priceValidFrom,
      formData.priceValidTill
    )
    if (rangeError) {
      setDateRangeError(rangeError)
      toast.error(rangeError, { autoClose: 2000 })
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return
    }

    setIsSubmitting(true)
    try {
      const supplierIds = selectedSuppliers.map((s) => s._id)
      const supplierNames = selectedSuppliers.map((s) => s.clientname || '')

      let productImageMeta = null
      if (productImageFile) {
        productImageMeta = await uploadProductImageIfNeeded(currentUserId)
        if (productImageMeta === null) return
      }

      let uploadedAttachments = []
      if (attachments.length > 0) {
        uploadedAttachments = await uploadAttachmentsIfNeeded(currentUserId)
        if (uploadedAttachments === null) return
      }

      const purchasePrice = parseFloat(formData.purchasePricePerUnit)
      const salesUomTrim = formData.salesUom.trim()
      const salesPriceRaw = String(formData.salesPricePerUnit || '').trim()
      const salesPrice =
        salesPriceRaw === '' ? null : parseFloat(salesPriceRaw)

      const payload = {
        action: 'create-price',
        createdBy: currentUserId,
        supplierId: supplierIds,
        supplierName: supplierNames,
        productName: formData.productName.trim(),
        purchaseUom: formData.purchaseUom.trim(),
        purchasePricePerUnit: purchasePrice,
        salesUom: salesUomTrim,
        salesPricePerUnit: salesPrice,
        priceValidFrom: dateInputToIsoStart(formData.priceValidFrom),
        priceValidTill: dateInputToIsoEnd(formData.priceValidTill),
        remarks: formData.remarks.trim()
      }

      if (uploadedAttachments.length > 0) {
        payload.attachments = uploadedAttachments
        payload.s3Keys = uploadedAttachments.map((a) => a.s3Key)
        payload.originalFileNames = uploadedAttachments.map((a) => a.originalFileName)
      }

      if (productImageMeta) {
        payload.productImage = productImageMeta
      }

      const procId = formData.procurementId.trim()
      if (procId) {
        payload.procurementId = procId
      }

      const response = await axios.post(API_URL, payload)
      const responseData = parsePriceApiResponse(response.data)

      if (responseData?.success === true) {
        toast.success(responseData.message || 'Price created successfully', {
          autoClose: 2000
        })
        setTimeout(() => navigate('/priceTracker'), 2000)
      } else {
        toast.error(responseData?.message || 'Failed to create price', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Create price error:', error)
      toast.error('An error occurred while creating price', { autoClose: 2000 })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Create Price</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/priceTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Supplier Name
                        </label>
                        <div className="col-12 col-md-8">
                          {selectedSuppliers.length > 0 && (
                            <div className="mb-2">
                              {selectedSuppliers.map((s) => (
                                <span
                                  key={s._id}
                                  className="badge badge-info mr-2 mb-1"
                                  style={{ fontSize: '12px', fontWeight: 550 }}
                                >
                                  {s.clientname}
                                  <span
                                    className="ml-2 crsr_pntr"
                                    onMouseDown={(ev) => {
                                      ev.preventDefault()
                                      setSelectedSuppliers((prev) =>
                                        prev.filter((x) => x._id !== s._id)
                                      )
                                    }}
                                    title="Remove"
                                  >
                                    <i className="fa-solid fa-xmark"></i>
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                isSuppliersLoading
                                  ? 'Loading suppliers...'
                                  : 'Type to search supplier and press Enter...'
                              }
                              value={supplierSearchTerm}
                              onChange={handleSupplierSearchChange}
                              onKeyDown={handleSupplierKeyDown}
                              onFocus={() => setShowSupplierDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowSupplierDropdown(false)
                                  setSelectedSupplierIndex(-1)
                                }, 200)
                              }}
                              autoComplete="off"
                              disabled={isSuppliersLoading}
                            />
                            {showSupplierDropdown &&
                              !isSuppliersLoading &&
                              filteredSuppliers.length > 0 && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    zIndex: 1000,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  {filteredSuppliers.map((supplier, index) => (
                                    <div
                                      key={supplier._id}
                                      onMouseDown={(ev) => {
                                        ev.preventDefault()
                                        handleSupplierSelect(supplier)
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f0f0f0',
                                        backgroundColor:
                                          index === selectedSupplierIndex
                                            ? '#e9ecef'
                                            : '#fff'
                                      }}
                                      onMouseEnter={() => setSelectedSupplierIndex(index)}
                                    >
                                      {supplier.clientname}
                                    </div>
                                  ))}
                                </div>
                              )}
                            {showSupplierDropdown &&
                              !isSuppliersLoading &&
                              supplierSearchTerm &&
                              filteredSuppliers.length === 0 && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: '#fff',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    padding: '8px 12px',
                                    zIndex: 1000,
                                    color: '#6c757d',
                                    fontSize: '13px'
                                  }}
                                >
                                  No suppliers found
                                </div>
                              )}
                          </div>
                          <small className="form-text text-muted">
                            You can select multiple suppliers. Ids and names are sent as parallel arrays.
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Product Name
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="productName"
                            className="form-control"
                            placeholder="Product Name"
                            value={formData.productName}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Purchase UOM <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="purchaseUom"
                            className="form-control"
                            value={formData.purchaseUom}
                            onChange={handleChange}
                            disabled={isSubmitting || isLoadingUnits}
                            required
                          >
                            <option value="">
                              {isLoadingUnits ? 'Loading units...' : 'Select Purchase UOM'}
                            </option>
                            {unitOptions.map((opt) => (
                              <option key={`purchase-${opt.id || opt.unit}`} value={opt.unit}>
                                {opt.unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Purchase price per unit <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="purchasePricePerUnit"
                            className="form-control"
                            placeholder="0"
                            value={formData.purchasePricePerUnit}
                            onChange={handleDecimalPriceChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Sales UOM
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="salesUom"
                            className="form-control"
                            value={formData.salesUom}
                            onChange={handleChange}
                            disabled={isSubmitting || isLoadingUnits}
                          >
                            <option value="">
                              {isLoadingUnits ? 'Loading units...' : 'Select Sales UOM'}
                            </option>
                            {unitOptions.map((opt) => (
                              <option key={`sales-${opt.id || opt.unit}`} value={opt.unit}>
                                {opt.unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Sales price per unit
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="salesPricePerUnit"
                            className="form-control"
                            placeholder="0 (optional)"
                            value={formData.salesPricePerUnit}
                            onChange={handleDecimalPriceChange}
                          />
                        </div>
                      </div>

                     

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Valid From
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="priceValidFrom"
                            className="form-control"
                            value={formData.priceValidFrom}
                            onChange={handleChange}
                            onBlur={handleDateBlur}
                            max={
                              isCompleteDateValue(formData.priceValidTill)
                                ? formData.priceValidTill
                                : undefined
                            }
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Valid Till
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="priceValidTill"
                            className={`form-control${
                              dateRangeError ? ' is-invalid' : ''
                            }`}
                            value={formData.priceValidTill}
                            onChange={handleChange}
                            onBlur={handleDateBlur}
                            min={
                              isCompleteDateValue(formData.priceValidFrom)
                                ? formData.priceValidFrom
                                : undefined
                            }
                            required
                          />
                          {dateRangeError ? (
                            <div className="invalid-feedback d-block">
                              {dateRangeError}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Remarks</label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="remarks"
                            className="form-control"
                            rows={3}
                            placeholder="Internal note for this price row"
                            value={formData.remarks}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Product image</label>
                        <div className="col-12 col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={productImageLabel}
                              readOnly
                            />
                            <div className="input-group-append">
                              <label
                                className="btn btn-blue mb-0" 
                                htmlFor="priceProductImage"
                                style={{ cursor: 'pointer' }}
                              >
                                Browse
                              </label>
                              {productImageFile ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger mb-0"
                                  onClick={() => setProductImageFile(null)}
                                >
                                  Clear
                                </button>
                              ) : null}
                              <input
                                id="priceProductImage"
                                type="file"
                                className="d-none"
                                accept="image/*"
                                onChange={handleProductImageChange}
                              />
                            </div>
                          </div>
                          
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Attachments
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={attachmentsLabel}
                              readOnly
                            />
                            <div className="input-group-append">
                              <label
                                className="btn btn-blue mb-0"
                                htmlFor="priceAttachments"
                                style={{
                                  cursor:
                                    attachments.length >= MAX_ATTACHMENTS
                                      ? 'not-allowed'
                                      : 'pointer',
                                  opacity:
                                    attachments.length >= MAX_ATTACHMENTS ? 0.6 : 1
                                }}
                              >
                                Add files
                              </label>
                              {attachments.length > 0 ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger mb-0"
                                  onClick={clearAllAttachments}
                                >
                                  Clear all
                                </button>
                              ) : null}
                              <input
                                id="priceAttachments"
                                type="file"
                                className="d-none"
                                multiple
                                accept={ATTACHMENT_ACCEPT}
                                disabled={attachments.length >= MAX_ATTACHMENTS}
                                onChange={handleAttachmentsChange}
                              />
                            </div>
                          </div>
                          <small className="form-text text-muted">
                            PDF, Excel, Word, and images. Max {MAX_ATTACHMENTS} files.
                          </small>
                          {attachments.length > 0 && (
                            <ul className="list-group mt-2" style={{ fontSize: '13px' }}>
                              {attachments.map((f, index) => (
                                <li
                                  key={`${f.name}-${f.size}-${index}`}
                                  className="list-group-item d-flex justify-content-between align-items-center py-2"
                                >
                                  <span
                                    className="text-truncate mr-2"
                                    title={f.name}
                                    style={{ maxWidth: '85%' }}
                                  >
                                    <i className="fa-solid fa-paperclip mr-1 text-muted" />
                                    {f.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeAttachment(index)}
                                    title="Remove file"
                                  >
                                    <i className="fa-solid fa-times" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Creating...' : 'Create'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


export default PriceTrackerAdd